<!--
  The PR header's stack control: "Stack 3 of 7 ▾", shown when the pull
  request is in a stack of two or more, as the dashboard finds stacks: a
  native GitHub stack, or pull requests built on one another's branches.
  It opens a menu of the stack, base first, each pull request a link to
  it on the view the page is on (`tab`), with what the dashboard's rail shows of it: its
  place, state, review, checks and the viewer's recorded progress (the
  panel is StackPanel). The
  menu says when the stack is found by branches, not GitHub's own, and
  when it is shown as the dashboard last read it. While the stack is
  being read the chip says so, where a stack is expected (`expected`);
  elsewhere nothing shows until there is one. When it could not be read,
  the chip opens why, with Try again. The rest of the page waits on none
  of it.
-->
<script lang="ts">
  import { controlCommand } from '../keys/control';
  import { stackMenuKeys } from '../keys/controls';
  import { keyboard } from '../keys/keyboard.svelte';
  import type { StackMenu, StackMenuPr } from '../logic/prStack';
  import type { PrTab } from '../router';
  import { statusOf, type Remote } from '../runtime/remote';
  import Popover from './Popover.svelte';
  import StackPanel from './StackPanel.svelte';

  interface Props {
    stack: Remote<StackMenu | null>;
    /** The view the page is on: a member opens on the same one. */
    tab: PrTab;
    /** Whether the menu (or, when the stack failed to load, why) is open. */
    open?: boolean;
    /** There is reason to think the pull request is in a stack: its loading is said. */
    expected?: boolean;
    /** Read the stack again. */
    onretry: () => void;
  }
  let { stack, tab, open = $bindable(false), expected = false, onretry }: Props = $props();

  /** The key that opens the menu, as the page binds it. */
  const menuKey = $derived(keyboard.key({ kind: 'open', menu: 'stack' }));

  const uid = Math.random().toString(36).slice(2, 8);
  let chip: HTMLButtonElement | null = $state(null);
  let list: HTMLElement | null = $state(null);

  const menu = $derived(stack.tag === 'Has' ? stack.value : null);
  const status = $derived(statusOf(stack, 'this stack'));
  /** There is something to open: the menu, or why there is none. */
  const shown = $derived(menu !== null || stack.tag === 'Failed');

  // Nothing to open any more (the pull request left its stack): the panel goes with it.
  $effect(() => {
    if (!shown && open) open = false;
  });

  function close(refocus: boolean) {
    open = false;
    if (refocus) chip?.focus();
  }

  /** The menu's own keys (see `stackMenuKeys`): Enter is the focused link's, Esc the panel's. */
  function key(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const c = controlCommand(stackMenuKeys, e);
    if (!c || c === 'open' || c === 'close') return;
    const items = [...(list?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const to = c === 'first' ? 0 : c === 'last' ? items.length - 1 : c === 'next' ? Math.min(at + 1, items.length - 1) : Math.max(at - 1, 0);
    e.preventDefault();
    e.stopPropagation();
    items[to]?.focus();
  }

  /** A plain click on the pull request the page already shows only closes the menu; with a modifier it opens as any link. */
  function pick(e: MouseEvent, r: StackMenuPr) {
    if (r.current && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
      e.preventDefault();
      close(true);
    } else close(false);
  }
</script>

{#if stack.tag === 'Loading'}
  {#if expected}
  <span class="stack-chip" role="status" aria-busy="true"><span class="spinner" aria-hidden="true"></span>Stack<span aria-hidden="true">…</span><span class="sr-only">: {status.summary}</span></span>
  {/if}
{:else if stack.tag === 'Failed'}
  <button
    bind:this={chip}
    type="button"
    class="stack-chip failed"
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-controls={open ? `stack-${uid}` : undefined}
    title={status.summary}
    onclick={() => (open = !open)}>Stack unavailable<span class="caret" aria-hidden="true">▾</span></button
  >
  {#if open && chip}
    <Popover anchor={chip} role="dialog" label="This pull request's stack" id="stack-{uid}" onclose={close}>
      <div class="sm-failed" role="status">
        <p>{status.summary}</p>
        {#if status.advice}<p class="muted">{status.advice}</p>{/if}
        <button type="button" class="btn small" onclick={() => { close(true); onretry(); }}>Try again</button>
      </div>
    </Popover>
  {/if}
{:else if menu}
  <button
    bind:this={chip}
    type="button"
    class="stack-chip"
    aria-haspopup="menu"
    aria-expanded={open}
    aria-controls={open ? `stack-${uid}` : undefined}
    aria-label="Stack: {menu.position} of {menu.count}. Show the stack"
    title="This pull request is {menu.position} of {menu.count} in its stack{menuKey ? ` (${menuKey})` : ''}"
    onclick={() => (open = !open)}>Stack <b>{menu.position}</b> of <b>{menu.count}</b><span class="caret" aria-hidden="true">▾</span></button
  >
  {#if open && chip}
    <Popover anchor={chip} role="menu" label="Stack of {menu.count} pull requests, base first{menu.inferred ? ', found from branches' : ''}" id="stack-{uid}" initial="[aria-current='page']" onclose={close}>
      <!-- svelte-ignore a11y_no_static_element_interactions (the keys move between the menu's items, which take the focus) -->
      <div bind:this={list} onkeydown={key}>
        <StackPanel
          {menu}
          {tab}
          {status}
          recheck={stack.tag === 'Has' && stack.freshness.kind === 'stale' && stack.freshness.refresh.kind === 'failed'}
          onpick={pick}
          {onretry}
        />
      </div>
    </Popover>
  {/if}
{/if}

<style>
  .stack-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    border: 1px solid var(--line);
    border-radius: 30px;
    background: var(--surface);
    color: var(--fg);
    font: inherit;
    font-size: 12px;
    line-height: 1.6;
    padding: 1px 10px;
    white-space: nowrap;
  }
  .stack-chip b {
    font-variant-numeric: tabular-nums;
  }
  button.stack-chip:hover,
  button.stack-chip[aria-expanded='true'] {
    border-color: var(--accent);
    background: var(--soft);
  }
  .stack-chip.failed {
    border-color: var(--warn);
    background: var(--warnbg);
    color: var(--warn);
  }
  .caret {
    margin-left: 2px;
    font-size: 10px;
    opacity: 0.7;
  }
  .sm-failed p {
    margin: 0;
  }
  .muted {
    color: var(--muted);
  }
  .sm-failed {
    width: min(360px, calc(100vw - 46px));
    display: grid;
    gap: 8px;
    justify-items: start;
    padding: 6px 8px;
  }
</style>
